'use client';

import { useState } from 'react';
import { RefreshCw, Clock, Mic, Zap, Timer, Lightbulb, CheckCircle } from 'lucide-react';
import ToolHero from '@/components/tools/ToolHero';
import ToolCTA from '@/components/tools/ToolCTA';
import CopyButton from '@/components/tools/CopyButton';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type Goal = 'new-role' | 'clients' | 'investors' | 'networking';
type Audience = 'recruiters' | 'investors' | 'peers' | 'general';
type Tone = 'Professional' | 'Conversational' | 'Bold';

interface FormData {
  fullName: string;
  currentRole: string;
  industry: string;
  yearsExperience: string;
  topSkill: string;
  achievement: string;
  goal: Goal;
  audience: Audience;
  tone: Tone;
}

interface GeneratedPitch {
  label: string;
  description: string;
  text: string;
  targetWords: string;
  speakingTime: string;
  icon: React.ReactNode;
}

/* ------------------------------------------------------------------ */
/*  Utility helpers                                                    */
/* ------------------------------------------------------------------ */

function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function countWords(text: string): number {
  return text.split(/\s+/).filter(Boolean).length;
}

function estimateSpeakingTime(wordCount: number): string {
  const seconds = Math.round((wordCount / 150) * 60);
  if (seconds < 60) return `~${seconds}s`;
  const min = Math.floor(seconds / 60);
  const sec = seconds % 60;
  return sec > 0 ? `~${min}m ${sec}s` : `~${min}m`;
}

/* ------------------------------------------------------------------ */
/*  Goal & audience phrasing helpers                                   */
/* ------------------------------------------------------------------ */

function goalPhrase(goal: Goal): string {
  const map: Record<Goal, string[]> = {
    'new-role': [
      'exploring new opportunities where I can',
      'looking for my next role where I can',
      'seeking a position that lets me',
      'open to roles where I can',
      'actively pursuing opportunities to',
    ],
    clients: [
      'helping businesses',
      'working with organizations that want to',
      'partnering with companies looking to',
      'supporting teams that need to',
      'collaborating with businesses ready to',
    ],
    investors: [
      'seeking partners who believe in',
      'looking for investors aligned with',
      'building something that will',
      'raising capital to',
      'scaling a vision to',
    ],
    networking: [
      'always eager to connect with people who share a passion for',
      'love exchanging ideas about',
      'always looking to learn from others in',
      'enjoy building relationships around',
      'passionate about connecting with fellow professionals in',
    ],
  };
  return pickRandom(map[goal]);
}

function goalAsk(goal: Goal, industry: string, skill: string): string {
  const map: Record<Goal, string[]> = {
    'new-role': [
      `If you know of any teams looking for someone with deep expertise in ${skill}, I'd love to chat.`,
      `I'm actively exploring roles in ${industry} -- if you or anyone in your network is hiring, I'd welcome the conversation.`,
      `I'd appreciate any introductions to hiring managers or teams working in ${industry}.`,
      `If your company is growing its ${industry} team, I'd love to learn more.`,
      `I'd be grateful for any leads on ${industry} roles that value ${skill}.`,
    ],
    clients: [
      `If your organization is looking to level up its ${skill} capabilities, let's talk.`,
      `I'd love to explore how I can help your team achieve similar results in ${industry}.`,
      `If ${skill} is on your roadmap, I'd be happy to share how I can help.`,
      `I work with companies in ${industry} who want to get better at ${skill} -- is that something you're focused on?`,
      `If you're facing challenges around ${skill}, I'd welcome the chance to discuss solutions.`,
    ],
    investors: [
      `I'm looking for strategic partners who see the potential in ${industry} -- would love to share more details.`,
      `If you're interested in ${industry} opportunities, I'd love to walk you through what we're building.`,
      `We're raising our next round and I'd welcome the chance to share our vision with you.`,
      `If ${industry} innovation excites you, let's set up time to talk.`,
      `I'm looking for investors who understand ${industry} and want to be part of something transformative.`,
    ],
    networking: [
      `I'd love to stay in touch -- feel free to connect with me on LinkedIn.`,
      `Let's grab coffee sometime and swap ideas about ${industry}.`,
      `I'm always happy to chat about ${skill} or ${industry} trends -- don't hesitate to reach out.`,
      `If you're working on anything interesting in ${industry}, I'd love to hear about it.`,
      `Let's keep this conversation going -- I think we could learn a lot from each other.`,
    ],
  };
  return pickRandom(map[goal]);
}

function audienceHook(audience: Audience, industry: string): string {
  const map: Record<Audience, string[]> = {
    recruiters: [
      `In ${industry}, the difference between a good hire and a great one is measurable impact.`,
      `You know how hard it is to find ${industry} talent that delivers from day one?`,
      `Every hiring manager wants someone who can hit the ground running in ${industry}.`,
      `Finding the right fit for a ${industry} team goes beyond skills on paper.`,
      `The best ${industry} professionals don't just fill roles -- they transform them.`,
    ],
    investors: [
      `The ${industry} market is ripe for disruption, and I'm here to make it happen.`,
      `There's a massive opportunity in ${industry} that most people are overlooking.`,
      `The next wave of ${industry} innovation starts with solving a problem nobody else is tackling.`,
      `In ${industry}, the companies that win are the ones that move fastest.`,
      `${industry} is at an inflection point, and the timing couldn't be better.`,
    ],
    peers: [
      `If you work in ${industry}, you know the challenges we face every day.`,
      `As fellow ${industry} professionals, we understand that results matter more than titles.`,
      `Working in ${industry} means constantly adapting and pushing boundaries.`,
      `We're all navigating the evolving ${industry} landscape together.`,
      `The ${industry} community thrives when we share insights and lift each other up.`,
    ],
    general: [
      `Have you ever wondered what makes a great ${industry} professional?`,
      `Let me tell you about the work that gets me excited every single day.`,
      `I believe everyone deserves to understand what ${industry} professionals really do.`,
      `Here's something most people don't realize about ${industry}.`,
      `The best part of working in ${industry} is the impact you can create.`,
    ],
  };
  return pickRandom(map[audience]);
}

/* ------------------------------------------------------------------ */
/*  Word banks for variation                                           */
/* ------------------------------------------------------------------ */

const passionVerbs = [
  'passionate about', 'dedicated to', 'driven by',
  'focused on', 'committed to', 'energized by',
];

const impactPhrases = [
  'delivering measurable results', 'driving meaningful impact',
  'creating lasting value', 'making a tangible difference',
  'producing real outcomes', 'generating transformative results',
];

const transitionPhrases = [
  'What sets me apart is', 'What I bring to the table is',
  'My edge is', 'The thing I do best is',
  'Where I really excel is', 'My superpower is',
];

const connectors = [
  'Along the way', 'Throughout my career', 'During that time',
  'In that journey', 'Over those years', 'Through that experience',
];

/* ------------------------------------------------------------------ */
/*  30-Second Pitch Templates (~75 words) - 6 variations               */
/* ------------------------------------------------------------------ */

function generate30Second(d: FormData): string {
  const industry = d.industry || 'my field';
  const years = d.yearsExperience ? `${d.yearsExperience} years` : 'several years';
  const skill = d.topSkill || 'solving complex problems';
  const achievement = d.achievement || `${pickRandom(impactPhrases)} for every team I've worked with`;

  const templates: Record<Tone, string[]> = {
    Professional: [
      `Hi, I'm ${d.fullName}. I'm a ${d.currentRole} with ${years} of experience in ${industry}. I specialize in ${skill}, and I've ${achievement}. I'm ${pickRandom(passionVerbs)} ${pickRandom(impactPhrases)}, and I'm currently ${goalPhrase(d.goal)} apply that same drive to new challenges.`,

      `My name is ${d.fullName}, and I'm a ${d.currentRole} in the ${industry} space. Over the past ${years}, I've built deep expertise in ${skill}. Most recently, I ${achievement}. I'm currently ${goalPhrase(d.goal)} continue ${pickRandom(impactPhrases)}.`,

      `I'm ${d.fullName}, a ${d.currentRole} with ${years} in ${industry}. ${pickRandom(transitionPhrases)} my ability to leverage ${skill} to drive results. I've ${achievement}, and I'm ${goalPhrase(d.goal)} bring that track record to the right opportunity.`,

      `Hello, I'm ${d.fullName}. As a ${d.currentRole} with ${years} of ${industry} experience, I'm ${pickRandom(passionVerbs)} ${skill}. I've ${achievement}, which reflects my commitment to excellence. I'm now ${goalPhrase(d.goal)} make an even bigger impact.`,

      `${d.fullName} here. I've spent ${years} as a ${d.currentRole} in ${industry}, where I've honed my expertise in ${skill}. A highlight of my career has been when I ${achievement}. I'm currently ${goalPhrase(d.goal)} take on new challenges that demand that same level of impact.`,

      `I'm ${d.fullName}, and I work as a ${d.currentRole} in ${industry}. With ${years} of experience focused on ${skill}, I've ${achievement}. I'm ${pickRandom(passionVerbs)} ${pickRandom(impactPhrases)} and currently ${goalPhrase(d.goal)} contribute at a high level.`,
    ],
    Conversational: [
      `Hey, I'm ${d.fullName}! I'm a ${d.currentRole} and I've been working in ${industry} for about ${years}. What really lights me up is ${skill}. One of the things I'm most proud of is that I've ${achievement}. Right now, I'm ${goalPhrase(d.goal)} keep doing impactful work.`,

      `Hi there! I'm ${d.fullName}. I've been a ${d.currentRole} in ${industry} for ${years}, and honestly, I love what I do. My sweet spot is ${skill}, and I've gotten to do some pretty cool things -- like when I ${achievement}. These days, I'm ${goalPhrase(d.goal)} take things to the next level.`,

      `Nice to meet you! I'm ${d.fullName}, a ${d.currentRole} with about ${years} in ${industry}. If you ask me what gets me out of bed in the morning, it's ${skill}. I've ${achievement}, which was a real career highlight. I'm currently ${goalPhrase(d.goal)} find my next challenge.`,

      `So, I'm ${d.fullName} -- I'm a ${d.currentRole} who's been in the ${industry} world for ${years}. The thing I enjoy most is ${skill}. Recently, I ${achievement}, and it reminded me why I got into this field. Now I'm ${goalPhrase(d.goal)} keep that momentum going.`,

      `Hi! ${d.fullName} here. I've spent ${years} as a ${d.currentRole} in ${industry}, and what I really geek out on is ${skill}. One of my proudest moments was when I ${achievement}. I'm ${goalPhrase(d.goal)} do more of that kind of work.`,

      `I'm ${d.fullName}, and I'm a ${d.currentRole} who's been in ${industry} for ${years}. My thing? ${skill}. I once ${achievement}, and that experience shaped how I approach everything. I'm ${goalPhrase(d.goal)} find the right fit for what I do best.`,
    ],
    Bold: [
      `I'm ${d.fullName}, and I'm a ${d.currentRole} who doesn't settle for average. In ${years} in ${industry}, I've made ${skill} my weapon of choice. The proof? I've ${achievement}. I'm ${goalPhrase(d.goal)} bring that same intensity to something that matters.`,

      `${d.fullName}. ${d.currentRole}. ${years} in ${industry}. I've built my career around ${skill}, and the results speak for themselves -- I've ${achievement}. I'm ${goalPhrase(d.goal)} raise the bar even higher.`,

      `You want results in ${industry}? That's what I do. I'm ${d.fullName}, a ${d.currentRole} with ${years} of experience and a track record in ${skill}. I've ${achievement}, and I'm just getting started. I'm ${goalPhrase(d.goal)} make an outsized impact.`,

      `I'm ${d.fullName}, a ${d.currentRole} who's spent ${years} proving that ${skill} can transform ${industry}. I've ${achievement} -- not by playing it safe, but by pushing limits. I'm ${goalPhrase(d.goal)} do it again, bigger and better.`,

      `${years}. ${d.currentRole}. One mission: ${pickRandom(impactPhrases)}. I'm ${d.fullName}, and ${skill} isn't just what I do -- it's what I dominate. I've ${achievement}. Now I'm ${goalPhrase(d.goal)} bring that fire to the next challenge.`,

      `Hi, I'm ${d.fullName}. While most ${d.currentRole}s play it safe, I've spent ${years} in ${industry} going all-in on ${skill}. The result? I've ${achievement}. I'm ${goalPhrase(d.goal)} keep pushing what's possible.`,
    ],
  };

  return pickRandom(templates[d.tone]);
}

/* ------------------------------------------------------------------ */
/*  45-Second Pitch Templates (~110 words) - 6 variations              */
/* ------------------------------------------------------------------ */

function generate45Second(d: FormData): string {
  const industry = d.industry || 'my field';
  const years = d.yearsExperience ? `${d.yearsExperience} years` : 'several years';
  const skill = d.topSkill || 'solving complex problems';
  const achievement = d.achievement || `${pickRandom(impactPhrases)} for every team I've been part of`;
  const hook = audienceHook(d.audience, industry);

  const templates: Record<Tone, string[]> = {
    Professional: [
      `${hook} Hi, I'm ${d.fullName}, a ${d.currentRole} with ${years} of experience in ${industry}. I specialize in ${skill}, and throughout my career, I've been ${pickRandom(passionVerbs)} ${pickRandom(impactPhrases)}. One of my most significant accomplishments has been ${achievement}. This experience reinforced my belief that the right combination of expertise and execution can transform any organization. I'm currently ${goalPhrase(d.goal)} apply my skills to new challenges. ${goalAsk(d.goal, industry, skill)}`,

      `Hi, I'm ${d.fullName}. For the past ${years}, I've worked as a ${d.currentRole} in ${industry}, where I've developed deep expertise in ${skill}. ${hook} ${pickRandom(connectors)}, I've ${achievement}, which taught me the power of combining strategic thinking with hands-on execution. ${pickRandom(transitionPhrases)} my ability to see the big picture while delivering on the details. I'm ${goalPhrase(d.goal)} bring that perspective to a team that values impact. ${goalAsk(d.goal, industry, skill)}`,

      `My name is ${d.fullName}, and I'm a ${d.currentRole} with ${years} in the ${industry} sector. My core expertise is in ${skill}, and I've consistently used it to drive measurable outcomes for the organizations I've served. Most notably, I ${achievement}. ${hook} I'm ${pickRandom(passionVerbs)} finding new ways to apply my experience, and I'm currently ${goalPhrase(d.goal)} contribute at the highest level. ${goalAsk(d.goal, industry, skill)}`,

      `Hello, I'm ${d.fullName}. As a ${d.currentRole} with ${years} of experience in ${industry}, I bring a strong foundation in ${skill} and a track record of ${pickRandom(impactPhrases)}. ${pickRandom(connectors)}, I've ${achievement}. ${hook} I believe that ${skill} is the key differentiator in today's competitive landscape. I'm currently ${goalPhrase(d.goal)} leverage that expertise in a meaningful way. ${goalAsk(d.goal, industry, skill)}`,

      `I'm ${d.fullName}, a seasoned ${d.currentRole} in ${industry} with ${years} of hands-on experience. ${hook} My specialty is ${skill}, and I've put it to work in ways that matter -- for example, I've ${achievement}. That accomplishment reflects my approach: set ambitious goals, build the right strategy, and execute relentlessly. I'm now ${goalPhrase(d.goal)} make a similar impact elsewhere. ${goalAsk(d.goal, industry, skill)}`,

      `${d.fullName} here. I've built a ${years}-long career as a ${d.currentRole} in ${industry}, grounded in ${skill} and driven by a passion for ${pickRandom(impactPhrases)}. A career-defining moment was when I ${achievement}. ${hook} I'm committed to bringing that same level of excellence to every engagement. Right now, I'm ${goalPhrase(d.goal)} find the right opportunity to create maximum value. ${goalAsk(d.goal, industry, skill)}`,
    ],
    Conversational: [
      `${hook} I'm ${d.fullName}, and I've been working as a ${d.currentRole} in ${industry} for about ${years}. What I really love doing is ${skill} -- it's the part of my work that energizes me most. One of the things I'm most proud of is that I ${achievement}. It was one of those experiences that reminded me why I chose this path. These days, I'm ${goalPhrase(d.goal)} find my next adventure. ${goalAsk(d.goal, industry, skill)}`,

      `Hi! I'm ${d.fullName}. So here's the quick version: I'm a ${d.currentRole} with ${years} in ${industry}, and I absolutely love ${skill}. ${hook} The highlight of my career so far? I'd say it was when I ${achievement}. That project taught me so much about what's possible when you combine the right skills with the right team. I'm currently ${goalPhrase(d.goal)} do more work like that. ${goalAsk(d.goal, industry, skill)}`,

      `Nice to meet you! I'm ${d.fullName}. I've spent ${years} as a ${d.currentRole} in ${industry}, and honestly, ${skill} is where I come alive. ${hook} A moment I love looking back on is when I ${achievement} -- it was challenging, rewarding, and exactly the kind of work I want to keep doing. Right now, I'm ${goalPhrase(d.goal)} find the right match. ${goalAsk(d.goal, industry, skill)}`,

      `Hey there, I'm ${d.fullName}. I'm a ${d.currentRole} who's been in ${industry} for ${years}. My favorite thing to work on? Anything related to ${skill}. ${hook} Career highlight? Probably when I ${achievement}. It was one of those "this is why I do what I do" moments. Lately, I've been ${goalPhrase(d.goal)} channel that energy into something new. ${goalAsk(d.goal, industry, skill)}`,

      `I'm ${d.fullName}, a ${d.currentRole} with about ${years} in ${industry}. If there's one thing that makes my work feel meaningful, it's ${skill}. ${hook} I'm really proud of having ${achievement} -- it was a team effort and a turning point in my career. Now I'm ${goalPhrase(d.goal)} find my next chapter. ${goalAsk(d.goal, industry, skill)}`,

      `So, I'm ${d.fullName} -- ${d.currentRole}, ${industry} enthusiast, and someone who's spent ${years} getting really good at ${skill}. ${hook} The thing I love talking about most is how I ${achievement}. It showed me what's possible when you bring your best every day. I'm ${goalPhrase(d.goal)} keep growing. ${goalAsk(d.goal, industry, skill)}`,
    ],
    Bold: [
      `${hook} I'm ${d.fullName}. ${d.currentRole}. ${years} in ${industry}. And I don't do average. My specialty is ${skill}, and I've used it to ${achievement}. That's not luck -- that's what happens when expertise meets execution. I'm ${goalPhrase(d.goal)} bring that same intensity to something that matters. ${goalAsk(d.goal, industry, skill)}`,

      `You want to know what ${years} in ${industry} teaches you? That ${skill} separates the good from the great. I'm ${d.fullName}, a ${d.currentRole} who has ${achievement}. ${hook} I'm not here to blend in. I'm ${goalPhrase(d.goal)} make an impact that people remember. ${goalAsk(d.goal, industry, skill)}`,

      `I'm ${d.fullName}, and I've spent ${years} as a ${d.currentRole} proving that ${skill} can change the game in ${industry}. ${hook} The evidence? I've ${achievement}. While others play it safe, I go all in. I'm ${goalPhrase(d.goal)} replicate those results on an even bigger stage. ${goalAsk(d.goal, industry, skill)}`,

      `Let me be direct. I'm ${d.fullName}, a ${d.currentRole} with ${years} in ${industry}. ${skill} is my edge, and I've wielded it to ${achievement}. ${hook} I don't chase opportunities -- I create them. Right now, I'm ${goalPhrase(d.goal)} find a challenge worthy of my track record. ${goalAsk(d.goal, industry, skill)}`,

      `${d.fullName}. ${d.currentRole}. ${industry}. ${years} of refusing to settle. My weapon of choice is ${skill}, and I've used it to ${achievement}. ${hook} If you're looking for someone who delivers exceptional results, not excuses, we should talk. I'm ${goalPhrase(d.goal)} level up. ${goalAsk(d.goal, industry, skill)}`,

      `Here's what ${years} as a ${d.currentRole} in ${industry} has taught me: ${skill} isn't just a skill -- it's a superpower. I'm ${d.fullName}, and I've ${achievement}. ${hook} I'm ${pickRandom(passionVerbs)} pushing past what's expected and into what's extraordinary. I'm ${goalPhrase(d.goal)} do exactly that. ${goalAsk(d.goal, industry, skill)}`,
    ],
  };

  return pickRandom(templates[d.tone]);
}

/* ------------------------------------------------------------------ */
/*  60-Second Pitch Templates (~150 words) - 6 variations              */
/* ------------------------------------------------------------------ */

function generate60Second(d: FormData): string {
  const industry = d.industry || 'my field';
  const years = d.yearsExperience ? `${d.yearsExperience} years` : 'several years';
  const skill = d.topSkill || 'solving complex problems';
  const achievement = d.achievement || `${pickRandom(impactPhrases)} across every organization I've been part of`;
  const hook = audienceHook(d.audience, industry);
  const ask = goalAsk(d.goal, industry, skill);

  const templates: Record<Tone, string[]> = {
    Professional: [
      `${hook}

Hi, I'm ${d.fullName}, a ${d.currentRole} with ${years} of experience in ${industry}. I specialize in ${skill}, and I've built my career around turning that expertise into measurable outcomes for the organizations I serve.

${pickRandom(connectors)}, I've ${achievement}. This experience deepened my conviction that ${skill}, when applied strategically, can be a true differentiator. It also taught me the value of cross-functional collaboration and data-driven decision-making.

${pickRandom(transitionPhrases)} my ability to bridge the gap between strategy and execution. I don't just develop plans -- I implement them, measure them, and iterate until they deliver. I'm ${pickRandom(passionVerbs)} building teams and systems that produce sustainable results.

I'm currently ${goalPhrase(d.goal)} apply this track record of excellence to new challenges. ${ask}`,

      `My name is ${d.fullName}, and I've spent ${years} as a ${d.currentRole} in the ${industry} sector. My professional focus has been on ${skill}, and I'm proud of the impact I've been able to create.

${hook}

A defining moment in my career was when I ${achievement}. It required navigating complex challenges, aligning diverse stakeholders, and executing under pressure. The experience reinforced my approach: lead with clarity, act with purpose, and never lose sight of the end goal.

I bring a unique combination of technical depth in ${skill} and strategic vision that enables me to ${pickRandom(impactPhrases)}. I'm ${pickRandom(passionVerbs)} continuous improvement and believe that the best outcomes come from combining expertise with genuine curiosity.

Right now, I'm ${goalPhrase(d.goal)} leverage my experience in a new context. ${ask}`,

      `Hello, I'm ${d.fullName}. As a ${d.currentRole} with ${years} of ${industry} experience, I've dedicated my career to mastering ${skill} and using it to drive organizational success.

${hook}

One accomplishment that exemplifies my approach is when I ${achievement}. This required not just technical expertise, but also strong communication, stakeholder management, and the ability to adapt to changing conditions. The result was a testament to what's possible when the right skills meet the right mindset.

I'm known for my ability to take complex challenges and break them down into actionable strategies that deliver real results. Whether it's ${skill} or building high-performing teams, I approach every initiative with the same rigor and commitment.

I'm currently ${goalPhrase(d.goal)} make a significant impact. ${ask}`,

      `${hook}

I'm ${d.fullName}, a ${d.currentRole} who has spent ${years} in ${industry} building expertise in ${skill}. My career has been defined by a relentless focus on ${pickRandom(impactPhrases)} and a belief that excellence is a habit, not an accident.

A career highlight was when I ${achievement}. That experience was a masterclass in leadership, strategy, and execution. It showed me that the most impactful work happens at the intersection of deep expertise and bold action.

What drives me is the opportunity to solve problems that matter. I bring ${years} of lessons learned, a proven methodology, and an unshakeable commitment to quality. I'm ${pickRandom(passionVerbs)} finding innovative solutions and empowering the people around me to do the same.

I'm ${goalPhrase(d.goal)} apply my full range of experience to the right challenge. ${ask}`,

      `I'm ${d.fullName}, and for the past ${years}, I've served as a ${d.currentRole} in ${industry}. My area of specialization is ${skill}, and I've consistently used it to create value for the teams and organizations I work with.

${hook}

Perhaps my most significant professional achievement has been ${achievement}. This was a complex undertaking that required strategic planning, disciplined execution, and the ability to rally a team around a shared vision. The outcomes exceeded expectations and set a new benchmark.

I pride myself on being both a strategic thinker and a hands-on executor. I believe that ${pickRandom(impactPhrases)} requires more than good ideas -- it requires follow-through, accountability, and a willingness to learn from every outcome.

Today, I'm ${goalPhrase(d.goal)} bring this mindset and track record to bear on new opportunities. ${ask}`,

      `${hook}

Allow me to introduce myself. I'm ${d.fullName}, a ${d.currentRole} with ${years} of professional experience in ${industry}. ${skill} is at the core of what I do, and I've spent my career refining it and applying it to high-impact initiatives.

My proudest professional moment was when I ${achievement}. It was challenging, complex, and ultimately transformative. The experience shaped my philosophy: pursue excellence, embrace challenges, and always measure what matters.

What I bring to any organization is a blend of deep ${skill} expertise, strong leadership capabilities, and a track record of ${pickRandom(impactPhrases)}. I'm the kind of professional who doesn't just meet expectations but consistently raises the bar.

I'm currently ${goalPhrase(d.goal)} find a mission-aligned opportunity where I can make a lasting impact. ${ask}`,
    ],
    Conversational: [
      `${hook}

Hi, I'm ${d.fullName}! I've been a ${d.currentRole} in ${industry} for about ${years}, and honestly, I still get excited about it every day. My thing is ${skill} -- it's where I do my best work and where I've had the most impact.

The career moment I'm most proud of? When I ${achievement}. It was one of those experiences where everything came together -- the right challenge, the right team, and the right approach. It taught me that great results come from a combination of preparation and being willing to take smart risks.

What I love most about my work is that no two days are the same. I get to solve interesting problems, work with smart people, and see the tangible impact of ${skill} on real business outcomes.

These days, I'm ${goalPhrase(d.goal)} find my next great opportunity. ${ask}`,

      `Hey! I'm ${d.fullName}, and I'm a ${d.currentRole} who's been in ${industry} for ${years}. What gets me going is ${skill} -- I genuinely believe it's one of the most powerful things you can master in this field.

${hook}

Here's a story I love to share: I once ${achievement}. It wasn't easy, and there were definitely moments where I wasn't sure we'd pull it off. But we did, and the results were incredible. That experience shaped how I approach everything -- with curiosity, persistence, and a focus on what actually moves the needle.

I'm the type of person who dives deep into problems and won't rest until I've found a solution that works. But I also know that the best ideas come from collaboration and diverse perspectives.

I'm currently ${goalPhrase(d.goal)} build on what I've learned. ${ask}`,

      `Nice to meet you! I'm ${d.fullName}. Quick background: I've spent ${years} as a ${d.currentRole} in ${industry}, and ${skill} is really my bread and butter.

${hook}

If I had to pick one career highlight, it would be when I ${achievement}. It was one of those defining moments where you realize, "This is exactly what I should be doing." The combination of challenge and impact was everything I love about my work.

What people tend to appreciate about working with me is that I'm equal parts strategic and practical. I love the big picture, but I'm also the person who rolls up their sleeves and gets things done. I bring energy, ideas, and a genuine love for ${skill} to everything I take on.

Right now, I'm ${goalPhrase(d.goal)} take the next step. ${ask}`,

      `I'm ${d.fullName}, a ${d.currentRole} with about ${years} in ${industry}. If you're wondering what makes me tick, it's ${skill}. There's something incredibly satisfying about using that expertise to solve real problems.

${hook}

Let me give you an example. I once ${achievement}. It was the kind of project that stretches you in the best way -- demanding, rewarding, and full of lessons. It's the standard I hold myself to in everything I do.

People who work with me know I'm someone who cares deeply about quality and about the people I collaborate with. I believe that great work happens when you combine technical excellence with genuine teamwork and a healthy dose of humor.

I'm ${goalPhrase(d.goal)} find the right fit. ${ask}`,

      `Hi there! ${d.fullName} here. I'm a ${d.currentRole} and I've been in ${industry} for ${years}. My specialty? ${skill}. It's what I'm best at and it's what I enjoy most.

${hook}

One of my favorite career stories is about when I ${achievement}. It was challenging, but the kind of challenging that makes you better. The experience taught me the importance of staying focused, being adaptable, and never underestimating the power of a great team.

At the end of the day, I'm someone who wants to do work that matters. I bring a combination of ${skill} expertise, genuine enthusiasm, and a proven ability to ${pickRandom(impactPhrases)}.

I'm currently ${goalPhrase(d.goal)} find something that fits. ${ask}`,

      `So I'm ${d.fullName}! I'm a ${d.currentRole} who's been working in ${industry} for about ${years}. And the thing I'm most passionate about? ${skill}. Bar none.

${hook}

My career so far has had some amazing moments, but the one that stands out most is when I ${achievement}. It was proof that when you combine the right skills with the right attitude, the results can be extraordinary. I still draw on lessons from that experience every day.

What you'd notice about me right away is that I bring a lot of energy and curiosity to my work. I ask good questions, I dig into the details, and I always follow through. I love building things, solving problems, and making the people around me better.

I'm ${goalPhrase(d.goal)} keep that momentum going. ${ask}`,
    ],
    Bold: [
      `${hook}

I'm ${d.fullName}, a ${d.currentRole} with ${years} in ${industry}, and I don't do things halfway. My expertise is in ${skill}, and I've spent every year of my career proving just how powerful it can be.

Case in point: I ${achievement}. Not by following the playbook -- by rewriting it. That's my approach. Find the conventional wisdom, challenge it, and deliver something better.

${pickRandom(transitionPhrases)} my refusal to accept "good enough." In ${industry}, the gap between good and great is massive, and I live on the great side of that line. I bring ${years} of battle-tested experience, deep expertise in ${skill}, and an intensity that raises the performance of everyone around me.

I'm ${goalPhrase(d.goal)} bring that same energy to the next big challenge. ${ask}`,

      `Let me get straight to the point. I'm ${d.fullName}, a ${d.currentRole} who's spent ${years} dominating ${skill} in ${industry}.

${hook}

Results? I've ${achievement}. And that's not the ceiling -- that's the floor. Every project I take on gets my full commitment, and every team I work with learns what it means to perform at the highest level.

The ${industry} world doesn't need more people who maintain the status quo. It needs people who shatter it. That's my lane. I combine deep expertise in ${skill} with the kind of strategic vision that turns ambitious goals into accomplished milestones.

If you're looking for someone who will outwork, outthink, and outdeliver, you've found them. I'm ${goalPhrase(d.goal)} prove it on the biggest stage possible. ${ask}`,

      `${hook}

I'm ${d.fullName}. ${d.currentRole}. ${years} in ${industry}. And I'm here to make an impact, not an impression.

Here's what I've done: I've ${achievement}. That happened because I bring something most people don't -- an unrelenting commitment to ${skill} and a willingness to tackle the challenges everyone else avoids.

I've learned that ${industry} rewards the bold. The safe choices? They get safe results. I've built my career on the opposite -- taking calculated risks, pushing past limits, and delivering outcomes that redefine what's possible.

${pickRandom(transitionPhrases)} my ability to see opportunities where others see obstacles. I don't just solve problems -- I turn them into competitive advantages.

I'm ${goalPhrase(d.goal)} find the right arena. ${ask}`,

      `${years} in ${industry}. One role: ${d.currentRole}. One obsession: ${skill}. One standard: excellence. I'm ${d.fullName}, and I'm here to tell you that results matter more than resumes.

${hook}

My track record proves it. I've ${achievement}. That wasn't luck or timing -- it was the product of relentless focus, deep expertise, and the kind of execution that turns vision into reality.

I lead from the front. I make the hard calls. And I deliver. Whether it's ${skill} strategy, team building, or navigating the toughest challenges ${industry} throws at me, I bring a level of intensity and precision that's hard to match.

I'm ${goalPhrase(d.goal)} bring everything I have to the right opportunity. If you want predictable, I'm not your person. If you want extraordinary -- let's talk. ${ask}`,

      `Most ${d.currentRole}s will tell you they're good at ${skill}. I'm ${d.fullName}, and I've spent ${years} proving I'm exceptional at it.

${hook}

The evidence speaks for itself: I've ${achievement}. Behind that result is a philosophy that refuses to settle -- for mediocre strategies, for half-hearted execution, for anything less than the best possible outcome.

In ${industry}, I've seen what happens when people play it safe: they get left behind. I take a different approach. I lean into complexity, I challenge assumptions, and I build things that last.

${pickRandom(transitionPhrases)} my ability to combine ${skill} mastery with bold, decisive leadership. Teams I work with don't just meet targets -- they blow past them.

I'm ${goalPhrase(d.goal)} channel all of this into something transformative. ${ask}`,

      `The ${industry} landscape is shifting fast, and only the bold will survive. I'm ${d.fullName}, a ${d.currentRole} with ${years} of experience and an unshakable belief in the power of ${skill}.

${hook}

I haven't just watched the industry evolve -- I've shaped it. I've ${achievement}, and I did it by being willing to take the risks that others wouldn't. ${skill} isn't just a line on my resume -- it's the engine that drives everything I do.

What separates me from other ${d.currentRole}s? Vision without execution is a fantasy. I have both. I see where ${industry} is heading, and I have the expertise and the track record to lead the charge.

I'm ${goalPhrase(d.goal)} make waves. ${ask}`,
    ],
  };

  return pickRandom(templates[d.tone]);
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */

export default function ElevatorPitchContent() {
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    currentRole: '',
    industry: '',
    yearsExperience: '',
    topSkill: '',
    achievement: '',
    goal: 'new-role',
    audience: 'general',
    tone: 'Professional',
  });

  const [pitches, setPitches] = useState<GeneratedPitch[]>([]);
  const [hasGenerated, setHasGenerated] = useState(false);

  const updateField = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const generateAllPitches = () => {
    const pitch30 = generate30Second(formData);
    const pitch45 = generate45Second(formData);
    const pitch60 = generate60Second(formData);

    setPitches([
      {
        label: '30-Second Pitch',
        description: 'Quick intro for chance encounters and brief networking moments',
        text: pitch30,
        targetWords: '~75 words',
        speakingTime: '~30s',
        icon: <Zap className="w-5 h-5" />,
      },
      {
        label: '45-Second Pitch',
        description: 'Standard networking pitch for events, career fairs, and introductions',
        text: pitch45,
        targetWords: '~110 words',
        speakingTime: '~45s',
        icon: <Timer className="w-5 h-5" />,
      },
      {
        label: '60-Second Pitch',
        description: 'Full pitch with your ask -- ideal for interviews and targeted conversations',
        text: pitch60,
        targetWords: '~150 words',
        speakingTime: '~60s',
        icon: <Mic className="w-5 h-5" />,
      },
    ]);
    setHasGenerated(true);
  };

  const regenerateSingle = (index: number) => {
    const newPitches = [...pitches];
    if (index === 0) {
      newPitches[0] = { ...newPitches[0], text: generate30Second(formData) };
    } else if (index === 1) {
      newPitches[1] = { ...newPitches[1], text: generate45Second(formData) };
    } else {
      newPitches[2] = { ...newPitches[2], text: generate60Second(formData) };
    }
    setPitches(newPitches);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.fullName.trim() || !formData.currentRole.trim()) return;
    generateAllPitches();
  };

  const inputClass =
    'w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow text-gray-900';
  const labelClass = 'block text-sm font-semibold text-gray-700 mb-1.5';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-stone-100">
      <ToolHero
        title="Elevator Pitch Generator"
        description="Create a compelling 30-60 second professional elevator pitch. Perfect for networking events, interviews, and career fairs."
        category="Career Tools"
      />

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Input Form */}
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Build your elevator pitch
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Full Name */}
            <div>
              <label htmlFor="fullName" className={labelClass}>
                Your Name <span className="text-red-500">*</span>
              </label>
              <input
                id="fullName"
                type="text"
                required
                value={formData.fullName}
                onChange={(e) => updateField('fullName', e.target.value)}
                placeholder="e.g., Jane Smith"
                className={inputClass}
              />
            </div>

            {/* Current Role */}
            <div>
              <label htmlFor="currentRole" className={labelClass}>
                Current Role <span className="text-red-500">*</span>
              </label>
              <input
                id="currentRole"
                type="text"
                required
                value={formData.currentRole}
                onChange={(e) => updateField('currentRole', e.target.value)}
                placeholder="e.g., Product Manager, Software Engineer"
                className={inputClass}
              />
            </div>

            {/* Industry */}
            <div>
              <label htmlFor="industry" className={labelClass}>
                Industry
              </label>
              <input
                id="industry"
                type="text"
                value={formData.industry}
                onChange={(e) => updateField('industry', e.target.value)}
                placeholder="e.g., FinTech, Healthcare, SaaS"
                className={inputClass}
              />
            </div>

            {/* Years of Experience */}
            <div>
              <label htmlFor="yearsExperience" className={labelClass}>
                Years of Experience
              </label>
              <input
                id="yearsExperience"
                type="text"
                value={formData.yearsExperience}
                onChange={(e) => updateField('yearsExperience', e.target.value)}
                placeholder="e.g., 5, 10, 15"
                className={inputClass}
              />
            </div>

            {/* Top Skill */}
            <div className="sm:col-span-2">
              <label htmlFor="topSkill" className={labelClass}>
                Top Skill or Expertise
              </label>
              <input
                id="topSkill"
                type="text"
                value={formData.topSkill}
                onChange={(e) => updateField('topSkill', e.target.value)}
                placeholder="e.g., data-driven product strategy, full-stack development, enterprise sales"
                className={inputClass}
              />
            </div>

            {/* Biggest Achievement */}
            <div className="sm:col-span-2">
              <label htmlFor="achievement" className={labelClass}>
                Biggest Achievement
              </label>
              <textarea
                id="achievement"
                value={formData.achievement}
                onChange={(e) => updateField('achievement', e.target.value)}
                placeholder="e.g., led a product launch that generated $2M in first-year revenue, reduced customer churn by 35%"
                rows={2}
                className={`${inputClass} resize-none`}
              />
            </div>

            {/* What You're Looking For */}
            <div>
              <label htmlFor="goal" className={labelClass}>
                What are you looking for?
              </label>
              <select
                id="goal"
                value={formData.goal}
                onChange={(e) => updateField('goal', e.target.value)}
                className={inputClass}
              >
                <option value="new-role">A new role</option>
                <option value="clients">New clients</option>
                <option value="investors">Investors</option>
                <option value="networking">Networking / connections</option>
              </select>
            </div>

            {/* Target Audience */}
            <div>
              <label htmlFor="audience" className={labelClass}>
                Target Audience
              </label>
              <select
                id="audience"
                value={formData.audience}
                onChange={(e) => updateField('audience', e.target.value)}
                className={inputClass}
              >
                <option value="general">General audience</option>
                <option value="recruiters">Recruiters / Hiring Managers</option>
                <option value="investors">Investors</option>
                <option value="peers">Industry peers</option>
              </select>
            </div>

            {/* Tone */}
            <div className="sm:col-span-2">
              <label className={labelClass}>Tone</label>
              <div className="flex flex-wrap gap-3 mt-1">
                {(['Professional', 'Conversational', 'Bold'] as Tone[]).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => updateField('tone', t)}
                    className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${
                      formData.tone === t
                        ? 'bg-blue-600 text-white shadow'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Generate Button */}
          <button
            type="submit"
            className="mt-8 w-full sm:w-auto bg-gradient-to-r from-blue-600 to-blue-500 text-white font-bold py-3 px-10 rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-600 transition-all text-lg"
          >
            Generate Pitch
          </button>
        </form>

        {/* Results */}
        {hasGenerated && pitches.length > 0 && (
          <section className="mt-12 space-y-6">
            <h2 className="text-2xl font-extrabold text-gray-900">
              Your Elevator Pitches
            </h2>

            {pitches.map((pitch, idx) => {
              const wordCount = countWords(pitch.text);
              const speakTime = estimateSpeakingTime(wordCount);
              return (
                <div
                  key={idx}
                  className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8"
                >
                  {/* Header */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-blue-100 text-blue-600">
                        {pitch.icon}
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">
                          {pitch.label}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {pitch.description}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-50 text-blue-700">
                        {wordCount} words (target: {pitch.targetWords})
                      </span>
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-green-50 text-green-700 inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {speakTime}
                      </span>
                    </div>
                  </div>

                  {/* Pitch Text */}
                  <div className="bg-gray-50 rounded-xl p-5 mb-4">
                    <p className="text-gray-800 leading-relaxed whitespace-pre-line">
                      {pitch.text}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-3">
                    <CopyButton text={pitch.text} label="Copy" />
                    <button
                      onClick={() => regenerateSingle(idx)}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                      Regenerate
                    </button>
                  </div>
                </div>
              );
            })}
          </section>
        )}

        {/* Tips Section */}
        <section className="mt-16">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-amber-100 text-amber-600">
                <Lightbulb className="w-5 h-5" />
              </div>
              <h2 className="text-2xl font-extrabold text-gray-900">
                Tips for Delivering a Great Elevator Pitch
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  title: 'Practice out loud',
                  description:
                    'Reading your pitch and speaking it are completely different. Practice in front of a mirror, record yourself, or rehearse with a friend until it flows naturally.',
                },
                {
                  title: 'Speak at a natural pace',
                  description:
                    'Aim for about 150 words per minute. Speaking too fast makes you seem nervous, while speaking too slowly can lose your listener\'s attention.',
                },
                {
                  title: 'Lead with a hook',
                  description:
                    'Start with something that grabs attention -- a surprising statistic, a bold statement, or a question. You have about 5 seconds to earn the next 55.',
                },
                {
                  title: 'End with a clear ask',
                  description:
                    'Always close with what you want: a meeting, a referral, a business card exchange. An elevator pitch without an ask is just a monologue.',
                },
                {
                  title: 'Adapt to your audience',
                  description:
                    'Tailor your pitch on the fly based on who you are talking to. Recruiters care about skills and results. Peers care about shared challenges. Investors care about market opportunity.',
                },
                {
                  title: 'Use confident body language',
                  description:
                    'Make eye contact, stand tall, and use open gestures. Your body communicates as much as your words. Avoid crossing your arms or looking at the floor.',
                },
                {
                  title: 'Keep it conversational',
                  description:
                    'The best elevator pitches feel like the start of a conversation, not a rehearsed speech. Pause naturally, smile, and leave room for the other person to respond.',
                },
                {
                  title: 'Have multiple versions ready',
                  description:
                    'Not every situation calls for the same pitch. Have a 30-second version for quick encounters and a 60-second version for deeper conversations.',
                },
              ].map((tip, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">{tip.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {tip.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Educational Content */}
        <section className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mt-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">The Science Behind a Great Elevator Pitch</h3>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p>
              An elevator pitch is a concise, persuasive summary of who you are, what you do, and what you are looking for -- delivered in the time it takes to ride an elevator, typically 30 to 60 seconds. While the concept sounds simple, crafting an effective pitch requires careful thought about your audience, your unique value, and the impression you want to leave. According to career experts at <a href="https://www.indeed.com/career-advice/interviewing/how-to-give-an-elevator-pitch-examples" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Indeed</a>, the best elevator pitches are not memorized scripts but flexible frameworks that you can adapt in real time based on the person you are speaking with and the context of the conversation.
            </p>
            <p>
              Research in cognitive psychology suggests that people form first impressions within seven seconds, which means the opening of your pitch matters enormously. Leading with a question, a surprising statistic, or a bold statement activates curiosity and gives your listener a reason to keep paying attention. From there, the most effective pitches follow a simple structure: who you are, what problem you solve, proof that you can solve it (your key achievement), and a clear ask. This structure mirrors the persuasion frameworks taught at top business schools and recommended by organizations like the <a href="https://hbr.org/2018/10/how-to-nail-your-elevator-pitch" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Harvard Business Review</a>.
            </p>
            <p>
              One of the most common mistakes professionals make is treating the elevator pitch as a one-time performance rather than an ongoing practice. Your pitch should evolve as your career grows, and you should have multiple versions ready for different situations. A 30-second version works for casual introductions and chance encounters. A 60-second version is better suited for career fairs, interviews, and targeted networking conversations. Practicing out loud, recording yourself, and soliciting feedback from trusted colleagues are all proven ways to refine your delivery until it feels natural and confident.
            </p>
            <h4 className="text-lg font-bold text-gray-900 pt-2">Where to Use Your Elevator Pitch</h4>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Networking events and career fairs:</strong> These are the classic settings for an elevator pitch. Have your 30-second version ready for quick introductions and your 60-second version for deeper conversations at a booth or breakout session.</li>
              <li><strong>Job interviews:</strong> The &quot;Tell me about yourself&quot; question is essentially an invitation to deliver your elevator pitch. A polished response here sets the tone for the entire interview.</li>
              <li><strong>LinkedIn and professional profiles:</strong> Your pitch can be adapted into a compelling LinkedIn summary or professional bio that attracts recruiters and connections.</li>
              <li><strong>Everyday encounters:</strong> You never know when you will meet someone who can open a door for you. Having a confident, natural pitch ready means you are always prepared to make a strong impression.</li>
            </ul>
            <p className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-500">
              <strong>Sources &amp; Further Reading:</strong>{' '}
              <a href="https://www.indeed.com/career-advice/interviewing/how-to-give-an-elevator-pitch-examples" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Indeed: How to Give an Elevator Pitch (With Examples)</a>
              {' · '}
              <a href="https://hbr.org/2018/10/how-to-nail-your-elevator-pitch" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Harvard Business Review: How to Nail Your Elevator Pitch</a>
              {' · '}
              <a href="https://www.thebalancemoney.com/elevator-speech-examples-and-writing-tips-2061976" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">The Balance: Elevator Speech Examples and Writing Tips</a>
            </p>
          </div>
        </section>

        {/* CTA */}
        <ToolCTA
          headline="Your pitch opens doors. Your resume seals the deal."
          description="Create a tailored, ATS-optimized resume in minutes with Superpower Resume. Stand out to recruiters and land more interviews."
          buttonText="Build My Resume Free"
        />
      </div>
    </div>
  );
}
